import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { FiSend, FiSettings, FiCopy, FiRefreshCw } from 'react-icons/fi';
import MomentumAIIcon from '../../assets/images/Momentum AI Icon-bk.png';
import { marked } from 'marked';
import { handleUnauthorized } from '../../utils/apiClient';
import type { ChatMessage } from './CarrierFinderPage';
import '../commissionscan/CommissionScan.css';
import '../policycomparison/PolicyComparison.css';
import '../../styles/CarrierFinder.css';
const getToken = (): string => localStorage.getItem('toolboxToken') || '';

interface Toast { id: number; type: 'success' | 'error' | 'info'; message: string; }

interface Props {
  conversationId: string;
  initialChats: ChatMessage[];
  onGoToHistory: () => void;
  onGoToConfigure: () => void;
}

const CarrierFinderChat: React.FC<Props> = ({
  conversationId,
  initialChats,
  onGoToHistory,
  onGoToConfigure,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialChats);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [isReady, setIsReady] = useState<boolean | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const ctrl = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toastCounter = useRef(0);
  const activeIdRef = useRef<string | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastCounter.current;
    setToasts(p => [...p, { id, type, message }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);

  useEffect(() => {
    axios.get(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/checkstatus`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => setIsReady(res.data?.isReady ?? false))
      .catch(err => {
        if (err.response?.status === 401) handleUnauthorized();
        else setIsReady(false);
      });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const send = async () => {
    const q = input.trim();
    if (!q || streaming) return;
    if (ctrl.current) ctrl.current.abort();
    ctrl.current = new AbortController();

    const chatId = crypto.randomUUID();
    activeIdRef.current = chatId;

    setMessages(prev => [...prev, { chatId, question: q, answer: '' }]);
    setInput('');
    setStreaming(true);

    const formData = new FormData();
    formData.append('human_prompt', q);
    formData.append('chatId', chatId);
    formData.append('conversationId', conversationId);

    try {
      await fetchEventSource(`${(process.env.REACT_APP_PolicyAgent_Url || '')}/api/mycarrier-finder/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
        signal: ctrl.current.signal,
        onopen: async (res: Response) => {
          if (res.status === 401) { handleUnauthorized(); return; }
          if (!res.ok) {
            setStreaming(false);
            showToast('error', 'Failed to reach the carrier finder service.');
          }
        },
        onmessage: (event: { data: string; event?: string; id?: string }) => {
          try {
            const parsed = JSON.parse(event.data);
            const chunk: string = parsed.content || parsed.response || parsed.text || '';
            if (chunk && activeIdRef.current === chatId) {
              setMessages(prev =>
                prev.map(m => m.chatId === chatId ? { ...m, answer: m.answer + chunk } : m)
              );
            }
          } catch { /* non-JSON SSE line */ }
        },
        onclose: () => { setStreaming(false); activeIdRef.current = null; },
        onerror: () => { setStreaming(false); activeIdRef.current = null; },
      });
    } catch { /* aborted */ }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const copyMessage = async (text: string) => {
    try {
      const html = await marked(text) as string;
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('table').forEach(t => {
        t.setAttribute('style', 'border-collapse:collapse;width:100%;font-family:Arial,sans-serif;');
        t.querySelectorAll('th').forEach(th => th.setAttribute('style', 'border:2px solid #e3e3e3;padding:8px 10px;'));
        t.querySelectorAll('td').forEach(td => td.setAttribute('style', 'border:1px solid #e3e3e3;padding:7px 10px;'));
      });
      const plain = div.textContent || div.innerText || '';
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([div.innerHTML], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      showToast('success', 'Copied to clipboard!');
    } catch { showToast('error', 'Failed to copy.'); }
  };

  /* ── Loading — status check in progress ── */
  if (isReady === null) {
    return (
      <div className="cf-chat-status">
        <span className="pc-spinner" style={{ width: 20, height: 20 }} />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Checking configuration…</span>
      </div>
    );
  }

  /* ── Not configured ── */
  if (!isReady) {
    return (
      <div className="cf-chat-unconfigured">
        <div className="cf-chat-unconfigured__icon">
          <FiSettings size={32} />
        </div>
        <h3 className="cf-chat-unconfigured__title">Documents Not Configured</h3>
        <p className="cf-chat-unconfigured__body">
          Upload your carrier appetite PDFs first. The AI will analyze them to match risks with the right carriers.
        </p>
        <button className="pc-action-btn" onClick={onGoToConfigure}>
          Configure Documents
        </button>
      </div>
    );
  }

  /* ── Chat ── */
  return (
    <div className="cf-chat">

      {/* ── Messages ── */}
      <div className="cf-chat__messages">
        {messages.length === 0 && (
          <div className="cf-chat__empty">
            <div className="cf-chat__empty-icon">
              <img src={MomentumAIIcon} alt="AI" width={30} height={30} style={{ objectFit: 'contain' }} />
            </div>
            <h3 className="cf-chat__empty-title">My Carrier Finder</h3>
            <p className="cf-chat__empty-body">
              Describe a risk and I'll match it against your uploaded carrier appetite documents to find the best carriers.
            </p>
          </div>
        )}

        {messages.map((m, idx) => {
          const isLast = idx === messages.length - 1;
          return (
            <div key={m.chatId} className="cf-chat__pair">
              {/* Question */}
              <div className="cf-chat__question-wrap">
                <div className="cf-chat__question">{m.question}</div>
              </div>

              {/* Answer */}
              <div className="cf-chat__answer-wrap">
                <div className="cf-chat__answer-avatar">
                  <img src={MomentumAIIcon} alt="AI" width={18} height={18} style={{ objectFit: 'contain' }} />
                </div>
                <div className="cf-chat__answer-body">
                  {m.answer ? (
                    <div className="pc-markdown-body">
                      <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                        {m.answer}
                      </ReactMarkdown>
                      {streaming && isLast && <span className="pc-streaming-cursor" />}
                    </div>
                  ) : (
                    <span className="pc-streaming-cursor" />
                  )}

                  {/* Per-message toolbar — only on completed messages */}
                  {!streaming && m.answer && (
                    <div className="cf-chat__msg-toolbar">
                      <button className="pc-response-card__toolbar-btn" onClick={() => copyMessage(m.answer)}>
                        <FiCopy size={11} /> Copy
                      </button>
                      {isLast && (
                        <button className="pc-response-card__toolbar-btn" onClick={onGoToHistory}>
                          View History
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* ── New-chat restart button (after streaming done) ── */}
      {!streaming && messages.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
          <button
            className="pc-response-card__toolbar-btn"
            onClick={() => { if (ctrl.current) ctrl.current.abort(); setMessages([]); setInput(''); }}
          >
            <FiRefreshCw size={11} /> Clear Chat
          </button>
        </div>
      )}

      {/* ── Input ── */}
      <div className="cf-chat__input-area">
        <textarea
          className="cf-chat__input"
          placeholder="Describe a risk — e.g. 'owner-operator, 18-wheeler, 5 years experience…'"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={streaming}
          rows={2}
        />
        <button
          className="cf-chat__send-btn"
          onClick={send}
          disabled={streaming || !input.trim()}
          title="Send"
        >
          {streaming ? <span className="pc-spinner" style={{ width: 16, height: 16 }} /> : <FiSend size={16} />}
        </button>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center', margin: '4px 0 8px' }}>
        AI-generated output — verify important information before use.
      </p>

      {/* ── Toasts ── */}
      <div className="pc-toast">
        {toasts.map(t => (
          <div key={t.id} className={`pc-toast__item pc-toast__item--${t.type}`}>{t.message}</div>
        ))}
      </div>
    </div>
  );
};

export default CarrierFinderChat;

